import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Trash2, Loader2, Activity, KeyRound } from "lucide-react";

const ROLES = ["admin", "editor", "viewer"];

export default function RolesAudit() {
  const { user } = useApp();
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch((e) => console.error(e));
    api.get("/audit-logs").then(({ data }) => setLogs(data)).catch((e) => console.error(e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/users", form); toast.success("Team member added"); setForm({ name: "", email: "", password: "", role: "editor" }); load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };
  const removeUser = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  const changePw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setPwBusy(false); }
  };

  return (
    <div>
      <div className="mb-8"><h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B3B39]">Roles &amp; Audit Ledger</h1><p className="text-slate-600 mt-1">RBAC management &amp; tamper-evident activity log.</p></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="oj-card p-6">
          <h3 className="oj-h3 mb-5 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal-600" /> Team Members ({users.length})</h3>
          <div className="space-y-3 mb-6">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-2.5 border-b border-teal-900/40 last:border-0" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-3">
                  <div className="grid place-items-center w-9 h-9 rounded-full bg-teal-700 text-white font-bold text-sm">{u.name?.[0]}</div>
                  <div><div className="text-sm font-medium text-white">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase ${u.role === "admin" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-teal-50 text-teal-700 border border-teal-200"}`}>{u.role}</span>
                  {user?.role === "admin" && u.email !== "admin@oojjascorporate.com" && <button onClick={() => removeUser(u.id)} data-testid={`user-delete-${u.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
          {user?.role === "admin" && (
            <form onSubmit={addUser} className="pt-5 border-t border-teal-900/50 space-y-3" data-testid="add-user-form">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="oj-input" data-testid="user-name" />
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="oj-input" data-testid="user-email" />
                <input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="oj-input" data-testid="user-password" />
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="oj-input capitalize" data-testid="user-role">{ROLES.map((r) => <option key={r} className="bg-teal-950">{r}</option>)}</select>
              </div>
              <button disabled={saving} className="oj-btn-gold w-full !py-2.5 !text-sm" data-testid="user-add-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add Member</button>
            </form>
          )}
        </div>

        <div className="oj-card p-6">
          <h3 className="oj-h3 mb-5 flex items-center gap-2"><Activity className="w-5 h-5 text-amber-400" /> Audit Log</h3>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {logs.length === 0 ? <p className="text-slate-500 text-sm">No activity logged.</p> : logs.map((l, i) => (
              <div key={l.id} className="flex items-start gap-3 py-2 border-b border-teal-900/30 last:border-0" data-testid={`audit-log-${i}`}>
                <span className="w-2 h-2 rounded-full bg-teal-400 mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{l.detail || l.action}</div>
                  <div className="text-xs text-slate-500 font-mono flex items-center gap-2 flex-wrap">{l.actor} • {new Date(l.created_at).toLocaleString()} {l.ip && `• ${l.ip}`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="oj-card p-6 max-w-md" data-testid="change-password-card">
        <h3 className="oj-h3 mb-2 flex items-center gap-2"><KeyRound className="w-5 h-5 text-teal-600" /> Change My Password</h3>
        <p className="text-xs text-slate-500 mb-5">Your password is stored only as a bcrypt hash and is never reset by the server.</p>
        <form onSubmit={changePw} className="space-y-3" data-testid="change-password-form">
          <input required type="password" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="oj-input" data-testid="pw-current" />
          <input required type="password" minLength={8} placeholder="New password (min 8 characters)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="oj-input" data-testid="pw-new" />
          <button disabled={pwBusy} className="oj-btn-gold w-full !py-2.5 !text-sm" data-testid="pw-submit">{pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Update Password</button>
        </form>
      </div>
    </div>
  );
}
